// WizardProgressStepper component matching the HTML design exactly
import React from "react";

interface Step {
  id: string;
  label: string;
  shortLabel?: string;
}

interface WizardProgressStepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
}

const WizardProgressStepper: React.FC<WizardProgressStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  className = "",
}) => {
  const progressPercentage =
    currentStep === 1 ? 5 : ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <div
      className={`tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-bg-gray-50 dark:tw-bg-[#151f28] tw-border-b tw-border-gray-200 dark:tw-border-[#233648] ${className}`}
    >
      <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-w-full tw-max-w-full sm:tw-max-w-2xl tw-mx-auto tw-overflow-x-auto">
        {/* Background line */}
        <div className="tw-absolute tw-left-0 tw-top-[15px] tw-w-full tw-h-0.5 tw-bg-gray-200 dark:tw-bg-gray-700 tw-z-0"></div>

        {/* Progress line */}
        <div
          className="tw-absolute tw-left-0 tw-top-[15px] tw-h-0.5 tw-bg-primary tw-z-0 tw-transition-all tw-duration-300"
          style={{ width: `${progressPercentage}%` }}
        ></div>

        {/* Steps */}
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = index < currentStep - 1;
          const isCurrentOrCompleted = currentStep >= stepNumber;

          return (
            <div
              key={step.id}
              className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-group tw-cursor-pointer"
              onClick={() => onStepClick?.(stepNumber)}
            >
              {/* Circle */}
              <div
                className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-ring-2 tw-ring-white/80 dark:tw-ring-gray-900/80 tw-ring-offset-1 tw-ring-offset-transparent tw-shadow-sm tw-z-20 tw-transition-all ${
                  isCurrentOrCompleted
                    ? "tw-bg-primary tw-text-white"
                    : "tw-bg-gray-200 dark:tw-bg-gray-700 tw-text-gray-500 dark:tw-text-gray-400"
                }`}
              >
                {isCompleted ? (
                  <span className="material-symbols-outlined tw-text-base">
                    check
                  </span>
                ) : (
                  stepNumber
                )}
              </div>

              {/* Label - Desktop */}
              <span
                className={`tw-text-xs tw-font-medium tw-text-center tw-transition-colors tw-hidden sm:tw-block ${
                  isCurrentOrCompleted
                    ? "tw-text-primary tw-font-bold"
                    : "tw-text-gray-500 dark:tw-text-gray-400"
                }`}
              >
                {step.label}
              </span>

              {/* Label - Mobile */}
              <span
                className={`tw-text-xs tw-font-medium tw-text-center tw-transition-colors sm:tw-hidden ${
                  isCurrentOrCompleted
                    ? "tw-text-primary tw-font-bold"
                    : "tw-text-gray-500 dark:tw-text-gray-400"
                }`}
              >
                {step.shortLabel || step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WizardProgressStepper;
